import {
    ArrowBack as ArrowBackIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import { useIntegrationService } from '../../services/integration.service';
import { Platform } from '../../services/integration.types';
import IntegrationCreateComponent from './IntegrationCreate';
import PlatformSelectionGrid from './PlatformSelectionGrid';

interface IntegrationSelectModalProps {
    open: boolean;
    onClose: () => void;
    onJobCreated?: () => void;
    selectedPlatformName?: string;
    selectedImportType: string | null;
}

const IntegrationSelectModal: React.FC<IntegrationSelectModalProps> = ({
    open,
    onClose,
    onJobCreated,
    selectedPlatformName,
    selectedImportType,
}) => {
    const { t } = useTranslation();
    const { integrations } = useIntegrationsStatusContext();
    const integrationService = useIntegrationService();
    const theme = useTheme();
    const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);

    const { data: platforms = [], isLoading } = useQuery({
        queryKey: ['platforms'],
        queryFn: async () => {
            const allPlatforms = await integrationService.getAllPlatforms();
            return allPlatforms.filter((platform) => !platform.legacy);
        },
        enabled: open, // Only fetch when modal is open
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    // Pre-select platform if platformToSelect is provided
    useEffect(() => {
        if (selectedPlatformName && platforms.length > 0) {
            const platform = platforms.find((p) => p.name === selectedPlatformName);
            if (platform) {
                setSelectedPlatform(platform);
            }
        }
    }, [selectedPlatformName, platforms]);

    // Check if a platform already has an integration - only for exchange platforms
    const isIntegrationAdded = (platform: Platform): boolean => {
        if (!integrations || platform.platform_type !== 'exchange') return false;
        return integrations.some((integration) => integration.platformName === platform.name && integration.jobId);
    };

    const handlePlatformSelect = (platform: Platform) => {
        setSelectedPlatform(platform);
    };

    const handleBack = () => {
        setSelectedPlatform(null);
    };

    const handleClose = () => {
        setSelectedPlatform(null);
        onClose();
    };

    const handleJobCreated = () => {
        if (onJobCreated) {
            onJobCreated();
        }
        handleClose();
    };

    const handleFileUpload = () => {
        // Create a mock platform object for file upload
        const fileUploadPlatform: Platform = {
            name: 'excelUpload',
            display_name: 'File Upload',
            platform_type: 'excelUpload',
            logo_uri: '',
            legacy: false,
            position: 0,
            beta: false,
            custom_api_uri: '',
            custom_csv_uri: '',
        };

        setSelectedPlatform(fileUploadPlatform);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
            PaperProps={{
                sx: { 
                    width: '80%',
                    maxWidth: '1000px',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    bgcolor: theme => theme.palette.background.paper,
                    '& .MuiDialogContent-root': {
                        bgcolor: theme => theme.palette.background.paper,
                    }
                }
            }}
        >
            <DialogTitle sx={{ bgcolor: theme => theme.palette.background.paper }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1 
                }}>
                    {selectedPlatform !== null && (
                        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {selectedPlatform === null ? t('integrations.selectModal.title') : selectedPlatform?.display_name}
                    </Typography>
                    <IconButton aria-label="close" onClick={handleClose} sx={{ color: theme.palette.text.secondary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {selectedPlatform === null ? (
                    <PlatformSelectionGrid
                        platforms={platforms}
                        isLoading={isLoading}
                        onPlatformSelect={handlePlatformSelect}
                        onFileUploadSelect={handleFileUpload}
                        checkIfIntegrated={isIntegrationAdded}
                        integratedLabel="Integrated"
                        maxHeight={400} // Set a fixed height for the scrollable container
                    />
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <IntegrationCreateComponent
                            selectedImportType={selectedImportType}
                            platform={selectedPlatform}
                            onJobCreated={handleJobCreated}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default IntegrationSelectModal;
